<template>
  <video
      ref="videoRef"
      :id="videoId"
      :class="className"
      controls
      muted
      loop
      @error="handleVideoError"
  />
</template>

<script setup>
import { ref, defineProps, defineEmits, defineExpose, onMounted, onUnmounted, watch, nextTick } from 'vue';
import flvjs from 'flv.js';

// 定义组件属性
const props = defineProps({
  // 视频源地址
  src: {
    type: String,
    required: true
  },
  // 视频ID（用于唯一标识）
  videoId: {
    type: String,
    default: () => `video-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
  },
  // 自定义类名
  className: {
    type: String,
    default: 'video-player'
  },
  // 是否自动播放
  autoplay: {
    type: Boolean,
    default: false
  },
  // 是否静音
  muted: {
    type: Boolean,
    default: true
  },
  // 是否循环播放
  loop: {
    type: Boolean,
    default: true
  }
});

// 定义组件事件
const emit = defineEmits(['error', 'load', 'play', 'pause', 'ended']);

// 视频元素引用
const videoRef = ref(null);
// 播放器实例
let player = null;

// 检查是否支持FLV
const isFlvSupported = () => {
  return flvjs && flvjs.isSupported();
};

// 检查是否是FLV格式
const isFlvFormat = (url) => {
  return url && (url.endsWith('.flv') || url.includes('.flv?'));
};

// 初始化视频播放器
const initPlayer = async () => {
  if (!props.src || !videoRef.value) return;

  // 先销毁已存在的播放器实例
  destroyPlayer();

  // 根据视频格式选择播放方式
  if (isFlvFormat(props.src) && isFlvSupported()) {
    // FLV格式视频，使用flv.js播放
    await initFlvPlayer();
  } else {
    // 非FLV格式视频，使用HTML5原生播放
    initHtml5Player();
  }
};

// 初始化FLV播放器
const initFlvPlayer = async () => {
  try {
    // 等待DOM更新
    await nextTick();

    if (!videoRef.value) {
      console.warn('视频元素未找到，稍后重试...');
      setTimeout(initFlvPlayer, 200);
      return;
    }

    // 创建flv.js播放器实例
    player = flvjs.createPlayer({
      type: 'flv',
      url: props.src,
      isLive: false,
      cors: true,
      withCredentials: false
    });

    // 附加到视频元素
    player.attachMediaElement(videoRef.value);

    // 加载视频
    player.load();

    // 监听事件
    player.on('error', handleFlvError);
    player.on('loadedmetadata', () => emit('load'));
    player.on('play', () => emit('play'));
    player.on('pause', () => emit('pause'));
    player.on('ended', () => emit('ended'));

    // 如果设置了自动播放
    if (props.autoplay) {
      try {
        await player.play();
      } catch (error) {
        console.warn('自动播放失败，需要用户交互:', error);
      }
    }

  } catch (error) {
    console.error('初始化FLV播放器失败:', error);
    emit('error', error);
  }
};

// 初始化HTML5播放器
const initHtml5Player = () => {
  if (!videoRef.value) return;

  // 设置视频源
  videoRef.value.src = props.src;
  videoRef.value.muted = props.muted;
  videoRef.value.loop = props.loop;

  // 如果设置了自动播放
  if (props.autoplay) {
    videoRef.value.play().catch(error => {
      console.warn('自动播放失败，需要用户交互:', error);
    });
  }
};

// 销毁播放器
const destroyPlayer = () => {
  if (player) {
    try {
      player.destroy();
    } catch (error) {
      console.warn('销毁播放器失败:', error);
    }
    player = null;
  }
};

// 处理FLV播放器错误
const handleFlvError = (error) => {
  console.error('FLV播放器错误:', error);
  emit('error', error);
};

// 处理HTML5视频错误
const handleVideoError = (event) => {
  console.error('HTML5视频错误:', event);
  emit('error', event);
};

// 监听视频源变化
watch(() => props.src, () => {
  initPlayer();
}, { immediate: true });

// 监听是否静音变化
watch(() => props.muted, (newVal) => {
  if (videoRef.value) {
    videoRef.value.muted = newVal;
  }
});

// 监听是否循环播放变化
watch(() => props.loop, (newVal) => {
  if (videoRef.value) {
    videoRef.value.loop = newVal;
  }
});

// 生命周期钩子
onMounted(() => {
  initPlayer();
});

onUnmounted(() => {
  destroyPlayer();
});

// 暴露方法给父组件
defineExpose({
  play: () => {
    if (player) {
      return player.play();
    } else if (videoRef.value) {
      return videoRef.value.play();
    }
  },
  pause: () => {
    if (player) {
      player.pause();
    } else if (videoRef.value) {
      videoRef.value.pause();
    }
  },
  destroy: destroyPlayer
});
</script>

<style scoped>
.video-player {
  width: 100%;
  max-width: 100%;
  height: auto;
  margin-bottom: 10px;
}
</style>